/**
 * Centralised input validation for Indian-business identifiers.
 * Both client (this file) and server validate against the SAME regexes so
 * one source of truth catches typos. The server enforces; the client warns.
 *
 * Each validator returns `{ ok: true } | { ok: false, msg: string }`.
 * `validate*` are STRICT (used on submit). `looksLike*` are loose
 * (used in inline hints to avoid red text the moment the user starts typing).
 */

// ── Format regexes ──────────────────────────────────────────────────────
// PAN: 5 uppercase letters + 4 digits + 1 uppercase letter (10 chars)
//   4th char encodes entity type (P=person, C=company, ...). We allow any
//   uppercase letter — the gov. portal validates the exact char.
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// GSTIN: 2-digit state code + 10-char PAN + entity number + Z + checksum
//  (= 15 chars). 14th char must be Z. Last char is checksum (letter or digit).
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

// IFSC: 4 uppercase letters + "0" + 6 alphanumerics
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// MICR: 9 digits exactly
export const MICR_RE = /^[0-9]{9}$/;

// Indian mobile: 10 digits, must start with 6/7/8/9. Allows optional +91 prefix.
export const MOBILE_RE = /^(?:\+?91)?[6-9][0-9]{9}$/;

// Email: lightweight RFC-compliant-ish; server does the strict check.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Indian bank account no.: 9-18 digits (varies by bank).
export const ACCOUNT_NO_RE = /^[0-9]{9,18}$/;

// ── Public validators ───────────────────────────────────────────────────

const ok = { ok: true };

export const validatePAN = (v) => {
  if (!v) return ok;  // optional field
  return PAN_RE.test(v.toUpperCase())
    ? ok
    : { ok: false, msg: "Invalid PAN. Expected 10 chars: ABCDE1234F" };
};

export const validateGSTIN = (v) => {
  if (!v) return ok;
  return GSTIN_RE.test(v.toUpperCase())
    ? ok
    : { ok: false, msg: "Invalid GSTIN. Expected 15 chars: 27ABCDE1234F1Z5" };
};

export const validateIFSC = (v) => {
  if (!v) return ok;
  return IFSC_RE.test(v.toUpperCase())
    ? ok
    : { ok: false, msg: "Invalid IFSC. Expected 11 chars like HDFC0001234" };
};

export const validateMICR = (v) => {
  if (!v) return ok;
  return MICR_RE.test(v)
    ? ok
    : { ok: false, msg: "MICR code is exactly 9 digits" };
};

export const validateMobile = (v) => {
  if (!v) return ok;
  return MOBILE_RE.test(v.replace(/[\s-]/g, ""))
    ? ok
    : { ok: false, msg: "Mobile must be a 10-digit Indian number (starts 6-9)" };
};

export const validateEmail = (v) => {
  if (!v) return ok;
  return EMAIL_RE.test(v)
    ? ok
    : { ok: false, msg: "Invalid email address" };
};

export const validateAccountNo = (v) => {
  if (!v) return ok;
  return ACCOUNT_NO_RE.test(v.replace(/\s/g, ""))
    ? ok
    : { ok: false, msg: "Account number must be 9-18 digits" };
};

/** Run a batch of validators. Returns the first failure or `{ ok: true }`. */
export function validateAll(checks) {
  for (const [label, result] of checks) {
    if (result && !result.ok) {
      return { ok: false, msg: `${label}: ${result.msg}` };
    }
  }
  return ok;
}
